/**
 * "Show me this station's board", asked from another screen.
 *
 * The line strip is a list of stations and the question a tap on one asks is
 * always the same: when is the next train from there. The board lives in
 * another tab, and this app's router has no URLs to carry a station in — so
 * the request is left here and the board picks it up as it enters.
 *
 * Taken, not read: a request is consumed once, so coming back to the tab a
 * minute later does not drag the board to a station the person has finished
 * with. Only what the board needs is kept — the stop id, the name, and the
 * line ids that decide whether the board splits by direction.
 */
import { computed, ref } from 'vue';

const pending = ref(null);

/**
 * Ask the board to open at this station on its next entry.
 *
 * `destStopId` is the second half of a SHARED board — "Carnegie to State
 * Library" is one screen, not two, and a link that set the station and left
 * the destination to the recipient's own habit would land them somewhere the
 * sender never saw. Null, and absent, both mean "say nothing about it", which
 * leaves the board's own seeding rules alone.
 */
export function requestBoard(station, { destStopId = null } = {}) {
  const stopId = Number(station?.stopId);
  if (!Number.isFinite(stopId) || stopId <= 0) return null;
  const dest = Number(destStopId);
  pending.value = {
    stopId,
    name: String(station.name ?? ''),
    suburb: String(station.suburb ?? ''),
    lines: Array.isArray(station.lines) ? [...station.lines] : station.lines ?? [],
    destStopId: Number.isFinite(dest) && dest > 0 ? dest : null,
  };
  return pending.value;
}

/**
 * The request as something a screen can WATCH, without consuming it.
 *
 * `takeRequestedBoard` is right for a tap on another screen, which always
 * happens before the board is entered. A shared link is not: it can be applied
 * before the page mounts, while it is mounting, or long after it is already on
 * screen and showing something else. So the board consumes this reactively as
 * well as at mount and at entry, and all three routes end in the same call.
 *
 * THE PATTERN for the screens still to be wired: watch your own request ref,
 * consume it in one function, and call that function from the watcher, from
 * `onMounted` and from `onIonViewDidEnter`. See DeparturesPage.consumeRequest.
 */
export const requestedBoard = computed(() => pending.value);

/** The request, cleared. Null when nobody asked. */
export function takeRequestedBoard() {
  const asked = pending.value;
  pending.value = null;
  return asked;
}

/** Tests only. */
export function clearRequestedBoard() {
  pending.value = null;
}

// ── Follow a train once the board has it ─────────────────────────────────────
//
// "Follow 4:07" on the arrival notice opens the app on that station; the board
// then follows the row leaving at that minute. Kept here beside the board
// request it travels with, and taken once.

const followAsked = ref(null);

/** Ask the board to follow the train leaving `stopId` at `leaveMs` (± a minute and a half). */
export function requestFollowAt(stopId, leaveMs) {
  if (!(Number(stopId) > 0) || !Number.isFinite(leaveMs)) return;
  followAsked.value = { stopId: Number(stopId), leaveMs };
}

export const requestedFollow = computed(() => followAsked.value);

export function takeRequestedFollow() {
  const asked = followAsked.value;
  followAsked.value = null;
  return asked;
}
